/**
 * Textarea — Campo de texto multilinha dark theme
 * Suporte para auto-resize opcional
 */
import type { JSX } from 'preact';
import { useRef, useEffect } from 'preact/hooks';
import { T } from '../../styles/tokens';

interface TextareaProps {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  autoResize?: boolean;
  maxHeight?: number;
  style?: JSX.CSSProperties;
  className?: string;
  onKeyPress?: (e: KeyboardEvent) => void;
  onKeyDown?: (e: KeyboardEvent) => void;
  onFocus?: (e: FocusEvent) => void;
  onInput?: (e: Event) => void;
}

export function Textarea({ 
  label, 
  value, 
  onChange, 
  rows = 4, 
  placeholder, 
  required, 
  disabled, 
  autoFocus,
  autoResize = false,
  maxHeight = 120,
  style: extraStyle, 
  className, 
  onKeyPress,
  onKeyDown,
  onFocus,
  onInput,
}: TextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize handler
  useEffect(() => {
    if (autoResize && textareaRef.current) {
      const ta = textareaRef.current;
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, maxHeight) + 'px';
    }
  }, [value, autoResize, maxHeight]);

  const handleInput = (e: any) => {
    onChange(e.target.value);
    
    // Auto-resize
    if (autoResize) {
      const ta = e.target as HTMLTextAreaElement;
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, maxHeight) + 'px';
    }
    
    // Custom onInput handler
    if (onInput) {
      onInput(e);
    }
  };

  return (
    <div>
      {label && (
        <label style={{ display: 'block', fontSize: T.textSm, fontWeight: T.weightSemibold, color: T.textSecondary, marginBottom: 6 }}>
          {label}
        </label>
      )}
      <textarea
        ref={textareaRef}
        value={value}
        onInput={handleInput}
        onKeyPress={onKeyPress as any}
        onKeyDown={onKeyDown as any}
        onFocus={onFocus as any}
        rows={rows}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoFocus={autoFocus}
        class={className}
        style={{
          width: '100%',
          padding: '10px 12px',
          fontSize: T.textMd,
          color: T.text,
          background: T.bgInput,
          border: `1px solid ${T.borderHover}`,
          borderRadius: T.radiusMd,
          outline: 'none',
          resize: autoResize ? ('none' as any) : ('vertical' as any),
          fontFamily: 'inherit',
          boxSizing: 'border-box' as any,
          maxHeight: autoResize ? maxHeight : undefined,
          overflowY: autoResize ? ('auto' as any) : undefined,
          ...extraStyle,
        }}
      />
    </div>
  );
}
